import React from 'react';
import { Heart, CheckCircle, ShieldCheck, MapPin } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';

const AboutPage = () => {
  const { language, t } = useLanguage();

  const bulletPoints = language === 'da' ? [
    'Om badeværelset reelt er brugbart',
    'Om hotellet er tilgængeligt',
    'Om poolen kan bruges',
    'Om omgivelserne fungerer',
    'At ferien fungerer for begge parter'
  ] : [
    'Whether the bathroom is actually usable',
    'Whether the hotel is accessible',
    'Whether the pool can be used',
    'Whether the surroundings work well',
    'That the holiday works for both parties'
  ];

  const missionItems = language === 'da' ? [
    { title: 'Nøje udvalgt', desc: 'Hvert rejsemål og hotel er valgt med omhu.' },
    { title: 'Gennemtænkt', desc: 'Vi tænker på praksis og de udfordringer, der kan opstå.' },
    { title: 'Reel erfaring', desc: 'Valgt ud fra steder vi selv har besøgt eller nøje har udvalgt.' }
  ] : [
    { title: 'Carefully Selected', desc: 'Every destination and hotel is chosen with care.' },
    { title: 'Well-planned', desc: 'We think about practical details and potential challenges.' },
    { title: 'Real Experience', desc: 'Chosen based on places we have visited or carefully selected.' }
  ];

  return (
    <div className="min-h-screen bg-white relative">
      <SEO 
        title={language === 'da' ? "Om Alora Rejser | Historien bag handicapvenlige rejser" : "About Alora Rejser | The Story Behind Accessible Holidays"} 
        description={language === 'da' ? "Læs historien bag Alora Rejser. Stiftet af en tidligere social- og sundhedshjælper med over 10 års personlig erfaring med rejser for kørestolsbrugere og gangbesværede." : "Read the story behind Alora Rejser. Founded by a former healthcare assistant with over 10 years of personal travel experience with wheelchair users."}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>

      {/* Hero Section */}
      <section className="relative bg-[#99A18C] overflow-hidden pt-24 lg:pt-32">
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2 text-white text-center md:text-left py-12 lg:py-20">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-6 leading-tight">
                {language === 'da' ? 'Hjertet bag bureauet' : 'The Heart Behind the Agency'}
              </h1>
              <p className="text-xl md:text-2xl italic text-white/90 mb-8 max-w-xl">
                {language === 'da' ? (
                  "Født ud af et ønske om at gøre en reel forskel for dem, der elsker at rejse – uanset udfordringer."
                ) : (
                  "Born out of a desire to make a real difference for those who love to travel – regardless of challenges."
                )}
              </p>
              <div className="h-1 w-20 bg-white/30 mx-auto md:mx-0"></div>
            </div>
            
            <div className="w-full md:w-1/2 flex justify-center md:justify-end md:pr-16 items-end">
              <div className="relative group">
                {/* Decorative element behind the image */}
                <div className="absolute -inset-4 bg-white/10 rounded-3xl transition-transform duration-500"></div>
                
                {/* Main Image */}
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png" 
                  srcSet="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png 400w, https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png 800w"
                  sizes="(max-width: 768px) 320px, 400px"
                  alt="Alora Rejser" 
                  className="relative z-10 w-full max-w-[320px] h-auto object-contain rounded-2xl shadow-2xl"
                  style={{ maxHeight: '450px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif text-[#99A18C] mb-6">
                {language === 'da' ? 'Min historie' : 'My Story'}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'da' ? (
                  "Alora Rejser blev skabt ud fra et stort ønske om at gøre det nemt og overskueligt for kørestolsbrugere og gangbesværede at booke rejser, på lige fod med alle andre."
                ) : (
                  "Alora Rejser was created out of a great desire to make it easy and manageable for wheelchair users and people with reduced mobility to book holidays, on equal terms with everyone else."
                )}
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'da' ? (
                  "I over 10 år har jeg rejst sammen med min mand, som er fast kørestolsbruger. Det har givet mig en helt særlig forståelse for, hvor svært det faktisk kan være, at finde rejser der fungerer – ikke bare på papiret, men i praksis."
                ) : (
                  "For over 10 years, I have traveled with my husband, who is a full-time wheelchair user. This has given me a special understanding of how difficult it can actually be to find holidays that work – not just on paper, but in practice."
                )}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {language === 'da' ? (
                  "Jeg er tidl. uddannet social- og sundhedshjælper, og kombinationen af min faglige baggrund og vores egne erfaringer har lært mig at se de detaljer, der gør den store forskel for, at ens ferie fungerer. Det er netop de erfaringer, jeg bruger i Alora Rejser – så du trygt kan vælge en ferie, der fungerer for alle parter."
                ) : (
                  "I am a former trained healthcare assistant, and the combination of my professional background and our own experiences has taught me to see the details that make the big difference for your holiday to work. These are precisely the experiences I use in Alora Rejser – so you can safely choose a holiday that works for everyone."
                )}
              </p>
              <p className="text-gray-700 leading-relaxed mt-4 font-medium italic text-[#99A18C]">
                {language === 'da' ? (
                  "\"Min drøm er at gøre det nemmere at rejse – uden bekymringer, uden usikkerhed og uden kompromiser. For alle fortjener en ferie, hvor man kan slappe af og nyde – ikke en ferie hvor man skal kæmpe for, at få det til at fungere.\""
                ) : (
                  "\"My dream is to make travel easier – without worries, without uncertainty, and without compromises. Because everyone deserves a holiday where they can relax and enjoy – not a holiday where they have to fight to make it work.\""
                )}
              </p>
            </div>
            <div className="bg-stone-50 p-8 rounded-2xl border border-[#99A18C]/20 shadow-sm">
              <h3 className="text-xl font-bold text-[#99A18C] mb-4 flex items-center">
                <ShieldCheck className="mr-2 text-[#99A18C]" />
                {language === 'da' ? 'Det handler om:' : 'It is about:'}
              </h3>
              <ul className="space-y-3">
                {bulletPoints.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm italic text-emerald-600">
                {language === 'da' ? (
                  "\"For ellers ender man hurtigt i en rolle, hvor man er mere hjælper end medrejsende.\""
                ) : (
                  "\"Otherwise, you quickly end up in a role where you are more of a helper than a co-traveler.\""
                )}
              </p>
            </div>
          </div>

          {/* Mission Section */}
          <div className="bg-[#99A18C] text-white p-12 rounded-3xl mb-20 shadow-xl">
            <h2 className="text-3xl font-serif mb-8 text-center">
              {language === 'da' ? 'Min mission' : 'My Mission'}
            </h2>
            <p className="text-lg text-white/90 mb-8 text-center max-w-2xl mx-auto">
              {language === 'da' ? (
                "Jeg ønsker at skabe et sted, hvor du kan finde rejser på samme måde som hos et klassisk rejsebureau – men med den forskel, at du kan have ro i maven."
              ) : (
                "I want to create a place where you can find holidays in the same way as with a classic travel agency – but with the difference that you can have complete peace of mind."
              )}
            </p>
            <div className="grid sm:grid-cols-3 gap-8">
              {missionItems.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-xl font-bold mb-2">{item.title}</div>
                  <p className="text-white/80 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="prose prose-stone lg:prose-xl mx-auto text-center">
            <h3 className="text-2xl font-serif text-[#99A18C] mb-4">
              {language === 'da' ? 'Rejser der fungerer – i virkeligheden' : 'Holidays that work – in reality'}
            </h3>
            <p className="text-gray-700 mb-8">
              {language === 'da' ? (
                "Jeg udvælger kun rejser, hvor tilgængeligheden er gennemtænkt, og der er taget højde for både bolig og omgivelser. Du skal kunne føle dig tryg fra start til slut."
              ) : (
                "I only select holidays where accessibility is thoroughly planned, taking into account both accommodation and surroundings. You should feel safe from start to finish."
              )}
            </p>
            <div className="my-8 max-w-2xl mx-auto overflow-hidden rounded-2xl shadow-lg bg-white relative z-10">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/37e2d4d3-05e6-4650-b028-e382f9843d35.png"
                srcSet="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/37e2d4d3-05e6-4650-b028-e382f9843d35.png 600w, https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/37e2d4d3-05e6-4650-b028-e382f9843d35.png 1000w"
                sizes="(max-width: 768px) 100vw, 672px"
                alt={language === 'da' ? 'Alora Rejser tilgængelighed' : 'Alora Rejser accessibility'}
                className="w-full h-auto object-cover rounded-2xl bg-white block opacity-100"
                loading="lazy"
              />
            </div>
            <div className="inline-block p-6 bg-white border-2 border-dashed border-[#99A18C]/20 rounded-2xl">
              <p className="text-[#99A18C] font-medium mb-0">
                {language === 'da' ? (
                  "\"Min drøm er at gøre det nemmere at rejse – uden bekymringer, uden usikkerhed og uden kompromiser. For alle fortjener en ferie, hvor man kan slappe af og nyde – ikke en ferie hvor man skal kæmpe for, at få det til at fungere.\""
                ) : (
                  "\"My dream is to make travel easier – without worries, without uncertainty, and without compromises. Because everyone deserves a holiday where they can relax and enjoy – not a holiday where they have to fight to make it work.\""
                )}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
